import React, { createContext, useContext, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

const NavigationContext = createContext();

export const NavigationProvider = ({ children }) => {
  const history = useHistory();
  const [pendingHighlight, setPendingHighlight] = useState(null);

  const navigateWithHighlight = useCallback((path, highlightTarget = null, scrollOptions = {}) => {
    const currentPath = history.location.pathname;
    // Strip hash and query params from target path for comparison
    const targetPathname = path.split('#')[0].split('?')[0];
    const isOnSamePage = currentPath === targetPathname;

    // If we're already on the target page and have a highlight target
    if (isOnSamePage && highlightTarget) {
      // Directly scroll and highlight without navigation
      setTimeout(() => {
        const element = document.getElementById(highlightTarget) || 
                       document.querySelector(`[data-highlight="${highlightTarget}"]`);
        
        if (element) {
          // Scroll to element
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            ...scrollOptions
          });
          
          // Add highlight class
          element.classList.add('navigation-highlight');
          
          // Remove after animation
          setTimeout(() => {
            element.classList.remove('navigation-highlight');
          }, 3000);
        }
      }, 100);
      return;
    }

    // Store highlight target if provided for navigation
    if (highlightTarget) {
      sessionStorage.setItem('navigationHighlight', JSON.stringify({
        target: highlightTarget,
        timestamp: Date.now(),
        scrollOptions: {
          behavior: 'smooth',
          block: 'center',
          ...scrollOptions
        }
      }));
    }

    // Navigate only if we're not on the same page
    if (!isOnSamePage) {
      history.push(path);
    }
  }, [history]);

  const checkAndHighlight = useCallback(() => {
    const stored = sessionStorage.getItem('navigationHighlight');
    if (!stored) return;

    try {
      const { target, timestamp, scrollOptions } = JSON.parse(stored);
      
      // Only process if less than 5 seconds old (prevents stale highlights)
      if (Date.now() - timestamp > 5000) {
        sessionStorage.removeItem('navigationHighlight');
        return;
      }

      sessionStorage.removeItem('navigationHighlight');

      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById(target) || 
                       document.querySelector(`[data-highlight="${target}"]`);
        
        if (element) {
          // Scroll to element
          element.scrollIntoView(scrollOptions);
          
          // Add highlight class
          element.classList.add('navigation-highlight');
          
          // Remove after animation
          setTimeout(() => {
            element.classList.remove('navigation-highlight');
          }, 3000);
        }
      }, 100);
    } catch (error) {
      console.error('Error processing navigation highlight:', error);
      sessionStorage.removeItem('navigationHighlight');
    }
  }, []);

  return (
    <NavigationContext.Provider value={{ 
      navigateWithHighlight, 
      checkAndHighlight,
      pendingHighlight,
      setPendingHighlight 
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};